import * as React from "react";
import { useMemo } from "react";
import { Range } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import {
  TextB,
  TextItalic,
  Quotes,
  Code,
  ListBullets,
  ListNumbers,
  Image,
  DotsThree,
  LinkSimple,
  TextT,
  Layout,
} from "@phosphor-icons/react";
import { isList, toggleList } from "../commands/q-editor.controls";
import { CustomEditor } from "../slate";

interface QToolbarProps {
  displayAd?: () => void;
  isAdVisible: boolean;
  toggleImageModal: (value: boolean) => void;
  isImageModalOpen: boolean;
  canUploadImages?: boolean;
}
interface ToolbarControlsGroupProps {
  children?: React.ReactNode;
}
interface ToolbarControlProps {
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  isActive?: boolean;
  isDisabled?: boolean;
}

const ToolbarControlsGroup = ({
  children,
}: ToolbarControlsGroupProps): JSX.Element => (
  <div className="q-toolbar-controls-group flex-container">{children}</div>
);

const ToolbarControl = ({
  children,
  onClick,
  isActive,
  isDisabled,
}: ToolbarControlProps): JSX.Element => (
  <div
    className={`q-toolbar-control flex-container ${isActive && "active"}`}
    onClick={onClick}
    data-disabled={isDisabled}
  >
    {children}
  </div>
);

const Toolbar = ({
  displayAd,
  isAdVisible,
  isImageModalOpen,
  toggleImageModal,
  canUploadImages = true,
}: QToolbarProps) => {
  const editor: CustomEditor = useSlate() as CustomEditor;

  const isEditorFocused = useMemo(
    () => ReactEditor.isFocused(editor) && !!editor.selection,
    [editor.selection]
  );
  const isLinkDisabled = useMemo(
    () =>
      !isEditorFocused ||
      (!!editor.selection && Range.isCollapsed(editor.selection)),
    [isEditorFocused, editor.selection]
  );
  const isImageDisabled = useMemo(
    () =>
      !isEditorFocused ||
      (!!editor.selection && !Range.isCollapsed(editor.selection)),
    [isEditorFocused, editor.selection]
  );

  return (
    <div className="q-editor-toolbar flex-container">
      <ToolbarControlsGroup>
        <ToolbarControl
          onClick={() => editor.toggleBold()}
          isActive={editor.isBold()}
          isDisabled={!isEditorFocused}
        >
          <TextB weight="bold" size={16} />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleItalic()}
          isActive={editor.isItalic()}
          isDisabled={!isEditorFocused}
        >
          <TextItalic size={16} />
        </ToolbarControl>
      </ToolbarControlsGroup>
      <ToolbarControlsGroup>
        <ToolbarControl
          onClick={() => displayAd && displayAd()}
          isActive={isAdVisible}
        >
          <Layout size={16} weight="light" />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleCodeBlock()}
          isActive={editor.isCodeBlock()}
          isDisabled={!isEditorFocused}
        >
          <Code size={16} weight="light" />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleBlockQuote()}
          isActive={editor.isBlockQuote()}
          isDisabled={!isEditorFocused}
        >
          <Quotes weight="fill" size={16} />
        </ToolbarControl>
      </ToolbarControlsGroup>
      <ToolbarControlsGroup>
        <ToolbarControl
          onClick={() => editor.toggleHeader1()}
          isActive={editor.isHeader1()}
          isDisabled={!isEditorFocused}
        >
          <div className="unordered-list-icon">
            <span></span>
          </div>
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleOrderedHeader1()}
          isActive={editor.isOrderedHeader1()}
          isDisabled={!isEditorFocused}
        >
          <div className="ordered-list-icon"></div>
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleHeader2()}
          isActive={editor.isHeader2()}
          isDisabled={!isEditorFocused}
        >
          <TextT size={21} />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleHeader3()}
          isActive={editor.isHeader3()}
          isDisabled={!isEditorFocused}
        >
          <TextT size={16} />
        </ToolbarControl>
      </ToolbarControlsGroup>
      <ToolbarControlsGroup>
        <ToolbarControl
          onClick={() => toggleList(editor, "ordered-list")}
          isActive={isList(editor, "ordered-list")}
          isDisabled={!isEditorFocused}
        >
          <ListNumbers size={16} weight="light" />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => toggleList(editor, "unordered-list")}
          isActive={isList(editor, "unordered-list")}
          isDisabled={!isEditorFocused}
        >
          <ListBullets size={16} weight="light" />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => editor.toggleLink()}
          isActive={editor.isLink()}
          isDisabled={isLinkDisabled && !editor.isLink()}
        >
          <LinkSimple size={16} weight="light" />
        </ToolbarControl>
      </ToolbarControlsGroup>
      <ToolbarControlsGroup>
        <ToolbarControl
          onClick={() => editor.insertHorizontalLine()}
          isDisabled={!isEditorFocused}
        >
          <DotsThree />
        </ToolbarControl>
        <ToolbarControl
          onClick={() => toggleImageModal(!isImageModalOpen)}
          isDisabled={!canUploadImages || isImageDisabled}
        >
          <Image />
        </ToolbarControl>
      </ToolbarControlsGroup>
    </div>
  );
};

export default Toolbar;
